import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Lightbulb, Sparkles, TrendingUp, AlertTriangle, Target, 
  Leaf, RefreshCw, Lock, Info, Download, FileText
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Sankey, Tooltip, Layer, Rectangle, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const insightTypes = [
  { id: 'significant_issues', label: 'Significant Issues', icon: AlertTriangle, description: 'Identify dominant contributors' },
  { id: 'contributors', label: 'Contributors', icon: TrendingUp, description: 'Explain why processes dominate' },
  { id: 'sensitivity', label: 'Sensitivity', icon: Target, description: 'Assess robustness of results' },
  { id: 'eco_design', label: 'Eco-Design Direction', icon: Leaf, description: 'High-level improvement areas' }
];

export default function Phase4Interpretation({ project, onUpdate, isLocked }) {
  const [loading, setLoading] = useState(true);
  const [lciaResult, setLciaResult] = useState(null);
  const [goalScope, setGoalScope] = useState(null);
  const [insights, setInsights] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [lciaRes, goalRes, insightsRes] = await Promise.all([
        api.get(`/projects/${project.id}/lcia`).catch(() => ({ data: null })),
        api.get(`/projects/${project.id}/goal-scope`).catch(() => ({ data: null })),
        api.get(`/projects/${project.id}/interpretation/insights`).catch(() => ({ data: [] }))
      ]);
      setLciaResult(lciaRes.data);
      setGoalScope(goalRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Failed to load interpretation data');
    } finally {
      setLoading(false);
    }
  };

  const generateInsight = async (type) => {
    if (!lciaResult) {
      toast.error('LCIA results required');
      return;
    }

    setGenerating(type);
    try {
      const response = await api.post(`/projects/${project.id}/interpretation/insights`, {
        insight_type: type
      });
      setInsights([...insights, response.data]);
      toast.success('AI insight generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate insight');
    } finally {
      setGenerating(null);
    }
  };

  const handleExportPDF = async (format) => {
    setExporting(true);
    const formatLabels = {
      'full': 'Full LCA Report',
      'epd': 'EPD Format',
      'cbam': 'CBAM Format'
    };
    
    try {
      toast.info(`Generating ${formatLabels[format]}...`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/projects/${project.id}/export/pdf/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name.replace(/\s+/g, '_')}_${format.toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${formatLabels[format]} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${formatLabels[format]}`);
    } finally {
      setExporting(false);
    }
  };

  // Prepare Sankey data
  const prepareSankeyData = () => {
    if (!lciaResult) return { nodes: [], links: [] };

    const nodes = [];
    const links = [];
    let nodeIndex = 0;

    // Add source nodes (categories)
    const categories = Object.entries(lciaResult.contributions_by_category)
      .filter(([, value]) => value > 0);
    
    const categoryIndices = {};
    categories.forEach(([cat]) => {
      categoryIndices[cat] = nodeIndex;
      nodes.push({ name: cat.toUpperCase() });
      nodeIndex++;
    });

    // Add stage nodes
    const stages = Object.entries(lciaResult.contributions_by_stage)
      .filter(([, value]) => value > 0);
    
    const stageIndices = {};
    stages.forEach(([stage]) => {
      stageIndices[stage] = nodeIndex;
      nodes.push({ name: stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) });
      nodeIndex++;
    });

    // Add impact node
    const impactIndex = nodeIndex;
    nodes.push({ name: 'Total Impact' });

    // Create links from categories to impact
    categories.forEach(([cat, value]) => {
      links.push({
        source: categoryIndices[cat],
        target: impactIndex,
        value: parseFloat(value.toFixed(2))
      });
    });

    return { nodes, links };
  };

  const sankeyData = prepareSankeyData();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lciaResult) {
    return (
      <div className="space-y-6" data-testid="phase4-interpretation">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-muted">
            <Lightbulb className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">ISO Phase 4 – Interpretation</h2>
            <p className="text-muted-foreground">Draw conclusions and identify significant issues</p>
          </div>
        </div>
        <Alert className="border-accent/50 bg-accent/10">
          <Lock className="h-4 w-4 text-accent" />
          <AlertDescription>
            LCIA calculation (Phase 3) must be completed before interpretation.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="phase4-interpretation">
      {/* Phase Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-sky-100">
            <Lightbulb className="h-6 w-6 text-sky-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">ISO Phase 4 – Interpretation</h2>
            <p className="text-slate-500">AI-powered insights and conclusions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="default" 
                disabled={exporting}
                data-testid="export-pdf-btn" 
                className="bg-sky-600 hover:bg-sky-700 text-white"
              >
                {exporting ? (
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export PDF
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-white">
              <DropdownMenuLabel>PDF Export Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleExportPDF('full')}
                className="cursor-pointer py-3"
                data-testid="export-full-btn"
              >
                <FileText className="h-4 w-4 mr-3 text-sky-600" />
                <div>
                  <div className="font-medium">Full LCA Report</div>
                  <div className="text-xs text-slate-500">Complete report with all phases</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExportPDF('epd')}
                className="cursor-pointer py-3"
                data-testid="export-epd-btn"
              >
                <FileText className="h-4 w-4 mr-3 text-emerald-600" />
                <div>
                  <div className="font-medium">EPD Format</div>
                  <div className="text-xs text-slate-500">ISO 14025 Environmental Product Declaration</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExportPDF('cbam')}
                className="cursor-pointer py-3"
                data-testid="export-cbam-btn"
              >
                <FileText className="h-4 w-4 mr-3 text-amber-600" />
                <div>
                  <div className="font-medium">CBAM Format</div>
                  <div className="text-xs text-slate-500">EU Carbon Border Adjustment Mechanism</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Badge variant="secondary" className="text-sm bg-sky-100 text-sky-700">
            AI Enabled
          </Badge>
        </div>
      </div>

      {/* Disclaimer */}
      <Alert className="border-sky-200 bg-sky-50">
        <Info className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-xs text-slate-600">
          <strong>ISO 14044 Disclaimer:</strong> Interpretation as defined in ISO 14044; conclusions contingent on stated goal, scope, and data quality.
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="hotspots" data-testid="hotspots-tab">Hotspot Analysis</TabsTrigger>
          <TabsTrigger value="insights" data-testid="insights-tab">AI Insights</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Study Summary */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Study Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {goalScope && (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Study Goal</p>
                      <p className="font-medium capitalize">{goalScope.study_goal?.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Functional Unit</p>
                      <p className="font-medium">
                        {goalScope.functional_unit_value} {goalScope.functional_unit_unit} - {goalScope.functional_unit_description}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">System Boundary</p>
                      <p className="font-medium capitalize">{goalScope.system_boundary?.replace(/_/g, ' ')}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Key Results */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Key Results</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Total Impact</p>
                  <p className="text-3xl font-bold font-mono">
                    {lciaResult.total_impact.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-muted-foreground">{lciaResult.impact_unit}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Method</p>
                  <p className="font-medium">{lciaResult.method} ({lciaResult.method_version})</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Top Contributor</p>
                  {lciaResult.contributions_by_process[0] && (
                    <p className="font-medium">
                      {lciaResult.contributions_by_process[0].name} ({lciaResult.contributions_by_process[0].percentage.toFixed(1)}%)
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Hotspots Tab */}
        <TabsContent value="hotspots" className="space-y-6">
          {/* Sankey Diagram */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Impact Flow (Sankey)</CardTitle>
              <CardDescription>Visualize contribution flows to total impact</CardDescription>
            </CardHeader>
            <CardContent>
              {sankeyData.nodes.length > 0 ? (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <Sankey
                      data={sankeyData}
                      node={({ x, y, width, height, index, payload }) => (
                        <Rectangle
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill={index === sankeyData.nodes.length - 1 ? '#6366f1' : '#10b981'}
                          fillOpacity={0.9}
                        />
                      )}
                      link={{ stroke: '#27272a', strokeOpacity: 0.5 }}
                      nodePadding={50}
                      margin={{ top: 20, right: 200, bottom: 20, left: 20 }}
                    >
                      <Tooltip />
                    </Sankey>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No data available for Sankey diagram
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contribution Tree */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Contribution Tree</CardTitle>
              <CardDescription>Hierarchical breakdown of impacts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* By Category */}
                <div>
                  <h4 className="font-medium mb-3">By Category</h4>
                  <div className="space-y-2">
                    {Object.entries(lciaResult.contributions_by_category)
                      .filter(([, value]) => value > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([cat, value]) => {
                        const percentage = (value / lciaResult.total_impact * 100).toFixed(1);
                        return (
                          <div key={cat} className="flex items-center gap-3">
                            <span className="w-24 text-sm font-medium uppercase">{cat}</span>
                            <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="h-full bg-primary/80 flex items-center justify-end pr-2"
                              >
                                <span className="text-xs font-mono text-primary-foreground">
                                  {value.toFixed(2)} kg CO₂e
                                </span>
                              </motion.div>
                            </div>
                            <span className="w-16 text-right font-mono text-sm">{percentage}%</span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* By Stage */}
                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium mb-3">By Lifecycle Stage</h4>
                  <div className="space-y-2">
                    {Object.entries(lciaResult.contributions_by_stage)
                      .filter(([, value]) => value > 0)
                      .sort(([, a], [, b]) => b - a)
                      .map(([stage, value]) => {
                        const percentage = (value / lciaResult.total_impact * 100).toFixed(1);
                        return (
                          <div key={stage} className="flex items-center gap-3">
                            <span className="w-32 text-sm font-medium capitalize">
                              {stage.replace(/_/g, ' ')}
                            </span>
                            <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                className="h-full bg-secondary/80 flex items-center justify-end pr-2"
                              >
                                <span className="text-xs font-mono text-secondary-foreground">
                                  {value.toFixed(2)} kg CO₂e
                                </span>
                              </motion.div>
                            </div>
                            <span className="w-16 text-right font-mono text-sm">{percentage}%</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          {/* Generate Insights */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Generate AI Insights
              </CardTitle>
              <CardDescription>
                AI interprets results within ISO 14044 boundaries. Cannot modify data or make prescriptive claims.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {insightTypes.map((type) => {
                  const Icon = type.icon;
                  const existingInsight = insights.find(i => i.insight_type === type.id);
                  return (
                    <motion.div
                      key={type.id}
                      whileHover={{ scale: 1.02 }}
                      className={`p-4 rounded-lg border ${
                        existingInsight ? 'border-secondary/50 bg-secondary/5' : 'border-border'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-5 w-5 text-primary" />
                          <h4 className="font-medium">{type.label}</h4>
                        </div>
                        {existingInsight && (
                          <Badge variant="secondary" className="text-xs">Generated</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{type.description}</p>
                      <Button
                        size="sm"
                        variant={existingInsight ? "outline" : "default"}
                        onClick={() => generateInsight(type.id)}
                        disabled={generating === type.id}
                        data-testid={`generate-${type.id}-btn`}
                      >
                        {generating === type.id ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : existingInsight ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Regenerate
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate
                          </>
                        )}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Generated Insights */}
          {insights.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Generated Insights</h3>
              {insights.map((insight) => {
                const typeConfig = insightTypes.find(t => t.id === insight.insight_type);
                const Icon = typeConfig?.icon || Sparkles;
                return (
                  <motion.div
                    key={insight.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="glass-card">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Icon className="h-5 w-5 text-primary" />
                          {typeConfig?.label || insight.insight_type}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {insight.model_version}
                          </Badge>
                          <span className="text-xs">
                            {new Date(insight.timestamp).toLocaleString()}
                          </span>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-invert prose-sm max-w-none">
                          <div className="whitespace-pre-wrap text-sm leading-relaxed">
                            {insight.content}
                          </div>
                        </div>
                        {insight.references.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <p className="text-xs text-muted-foreground">
                              References: {insight.references.join(' | ')}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
